import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { MarkdownRenderService } from './markdown-render.service';

/**
 * Mode d'application de la mémoire
 * - MEM_ALWAYS: Toujours appliquer
 * - MEM_SMART: Appliquer intelligemment (défaut, recherche sémantique)
 * - MEM_MANUAL: Appliquer manuellement
 */
export type MemoryApplyMode = 'MEM_ALWAYS' | 'MEM_SMART' | 'MEM_MANUAL';

/**
 * Labels français pour l'affichage UI
 */
export const MemoryApplyModeLabels: Record<MemoryApplyMode, string> = {
  MEM_ALWAYS: 'Toujours appliquer',
  MEM_SMART: 'Appliquer intelligemment',
  MEM_MANUAL: 'Appliquer manuellement',
};

/**
 * Interface pour une mémoire de discussion (côté client)
 */
export interface DiscussionMemory {
  id: string;
  title: string;
  //
  // summary is rendered HTML (for display)
  summary: string;
  //
  // summaryRaw is the original markdown/text (for editing)
  summaryRaw?: string;
  discussionId?: string;
  createdAt?: string;
  type: 'discussion';
  score?: number;
  applyMode?: MemoryApplyMode;
}

@Injectable({
  providedIn: 'root'
})
export class KngMemoriesService {
  //
  // BehaviorSubject pour la liste des mémoires
  private memoriesSubject = new BehaviorSubject<DiscussionMemory[]>([]);

  //
  // BehaviorSubject pour la mémoire sélectionnée (MEM_MANUAL)
  private selectedMemoryIdSubject = new BehaviorSubject<string | null>(null);

  //
  // Observable public pour la souscription
  memories$ = this.memoriesSubject.asObservable();

  //
  // Observable pour la mémoire sélectionnée (ID)
  selectedMemoryId$ = this.selectedMemoryIdSubject.asObservable();

  private readonly $markdown = inject(MarkdownRenderService);

  constructor(private $http: HttpClient) {}

  /**
   * Retourne l'ID de la mémoire actuellement sélectionnée
   */
  get selectedMemoryId(): string | null {
    return this.selectedMemoryIdSubject.value;
  }

  /**
   * Toggle la sélection d'une mémoire (pour MEM_MANUAL)
   * Si déjà sélectionnée, la désélectionne
   */
  toggleSelectedMemory(id: string): void {
    const current = this.selectedMemoryIdSubject.value;
    this.selectedMemoryIdSubject.next(current === id ? null : id);
  }

  /**
   * Désélectionne la mémoire active
   */
  clearSelectedMemory(): void {
    this.selectedMemoryIdSubject.next(null);
  }

  /**
   * Retourne la mémoire sélectionnée (objet complet)
   */
  getSelectedMemory(): DiscussionMemory | null {
    const id = this.selectedMemoryIdSubject.value;
    if (!id) return null;
    return this.memoriesSubject.value.find(m => m.id === id) || null;
  }

  /**
   * Convertit le markdown en HTML pour le summary
   */
  private renderSummary(summary: string): string {
    return this.$markdown.render(summary);
  }

  /**
   * Applique le rendu markdown sur les mémoires
   */
  private processMemories(memories: DiscussionMemory[]): DiscussionMemory[] {
    return memories.map(mem => ({
      ...mem,
      summaryRaw: mem.summary,
      summary: this.renderSummary(mem.summary)
    }));
  }

  /**
   * Récupère la liste des mémoires de discussion
   */
  list(): Observable<DiscussionMemory[]> {
    return this.$http.get<DiscussionMemory[]>('/api/agent/memories', {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
      }
    }).pipe(
      map((memories: DiscussionMemory[]) => this.processMemories(memories)),
      tap((memories: DiscussionMemory[]) => {
        this.memoriesSubject.next(memories);
      }),
      catchError((error) => {
        console.error('❌ Error loading memories:', error);
        this.notify('Erreur lors du chargement des mémoires', 'danger', 'exclamation-triangle');
        return of([]);
      })
    );
  }

  /**
   * Mémorise la discussion active (génère synthèse + stocke embedding)
   * @param agent - Nom de l'agent
   * @param capturePrompt - Prompt personnalisé pour la synthèse (optionnel)
   */
  memorize(agent: string, capturePrompt?: string): Observable<any> {
    const body: any = { agent };
    if (capturePrompt) {
      body.capturePrompt = capturePrompt;
    }

    return this.$http.post('/api/agent/memories', body, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
      }
    }).pipe(
      tap((response: any) => {
        if (response.success && response.memory) {
          this.notify(`Mémorisé: "${response.memory.title}"`, 'success', 'fire');
        } else {
          this.notify('Discussion mémorisée', 'success', 'fire');
        }
        // Recharger la liste
        this.list().subscribe();
      }),
      catchError((error) => {
        if (error.status === 409) {
          // Discussion déjà mémorisée
          const errorData = error.error || {};
          this.notify(`Déjà mémorisé: "${errorData.memory?.title || 'cette discussion'}"`, 'warning', 'info-circle');
          return of({ error: 'Discussion already memorized', memory: errorData.memory });
        }
        const message = error.error || 'Erreur lors de la mémorisation';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of({ error: message });
      })
    );
  }

  /**
   * Supprime une mémoire de discussion
   * @param id - ID de la mémoire à supprimer
   */
  delete(id: string): Observable<any> {
    return this.$http.delete(`/api/agent/memories/${id}`, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        this.notify('Mémoire supprimée', 'success', 'trash3');
        //
        // Désélectionner si la mémoire supprimée était sélectionnée
        if (this.selectedMemoryIdSubject.value === id) {
          this.selectedMemoryIdSubject.next(null);
        }
        //
        // Mettre à jour localement
        const current = this.memoriesSubject.value;
        this.memoriesSubject.next(current.filter(m => m.id !== id));
      }),
      catchError((error) => {
        const message = error.error || 'Erreur lors de la suppression';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  /**
   * Met à jour le contenu d'une mémoire (title, body et/ou applyMode)
   * @param id - ID de la mémoire
   * @param title - Nouveau titre
   * @param body - Nouveau contenu
   * @param applyMode - Mode d'application (optionnel)
   */
  update(id: string, title: string, body: string, applyMode?: MemoryApplyMode): Observable<any> {
    const payload: { title: string; body: string; applyMode?: MemoryApplyMode } = { title, body };
    if (applyMode) {
      payload.applyMode = applyMode;
    }

    return this.$http.put(`/api/agent/memories/${id}`, payload, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        const current = this.memoriesSubject.value;
        const next = current.map(m => {
          if (m.id !== id) return m;
          return {
            ...m,
            title,
            summaryRaw: body,
            summary: this.renderSummary(body),
            ...(applyMode && { applyMode }),
          };
        });
        this.memoriesSubject.next(next);
        this.notify('Mémoire mise à jour', 'success', 'check-circle');
      }),
      catchError((error) => {
        const message = error.error || 'Erreur lors de la mise à jour';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  /**
   * Recherche sémantique dans les mémoires
   * @param query - Requête de recherche
   * @param limit - Nombre max de résultats
   */
  search(query: string, limit = 5): Observable<DiscussionMemory[]> {
    return this.$http.post<DiscussionMemory[]>('/api/agent/memories/search',
      { query, limit },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'ngsw-bypass': 'true',
          'Cache-Control': 'no-cache',
        }
      }
    ).pipe(
      map((memories: DiscussionMemory[]) => this.processMemories(memories)),
      catchError((error) => {
        console.error('❌ Error searching memories:', error);
        return of([]);
      })
    );
  }

  /**
   * Affiche une notification toast (style Shoelace)
   */
  private notify(message: string, variant = 'primary', icon = 'info-circle', duration = 3000) {
    const escapeHtml = (html: string) => {
      const div = document.createElement('div');
      div.textContent = html;
      return div.innerHTML;
    };

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration: duration,
      innerHTML: `
        <sl-icon name="${icon}" slot="icon"></sl-icon>
        ${escapeHtml(message)}
      `
    });

    document.body.append(alert);
    return (alert as any).toast();
  }
}

